/**
 * GunBuy custom icon set — stroke-based, 24px grid, 1.5px strokes, round caps.
 * One consistent style for every category and for the site-level marks that
 * lucide doesn't cover well (firearm silhouettes, cartridge, safe).
 *
 * Usage: <RifleIcon className="h-5 w-5 text-slate-900" />
 *        <CategoryIcon category="shotgun" className="h-6 w-6" />
 */
import * as React from "react";

function make(name, paths) {
  const Icon = React.forwardRef(({ className, strokeWidth = 1.5, ...props }, ref) => (
    <svg
      ref={ref}
      viewBox="0 0 24 24"
      width="24"
      height="24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      className={className}
      {...props}
      dangerouslySetInnerHTML={{ __html: paths }}
    />
  ));
  Icon.displayName = name;
  return Icon;
}

export const RifleIcon = make("RifleIcon", '<path d="M23 8.5H12V7H7.5L4 9l-2 1.5.5 4.5h2L6 11.5h2l.5 1.5h2.5l.5-2H14l1-1h8z"/><path d="M10 7l1.5-2.5"/>');
export const ShotgunIcon = make("ShotgunIcon", '<path d="M23 8H11V7H7.5L4 9l-2 1.5.5 4.5h2L6 11.5h2l.5 1.5h2.5l.5-2H14v-.5h9z"/><path d="M13 9.25h10"/>');
export const HandgunIcon = make("HandgunIcon", '<path d="M3.5 6.5h17.5V10h-8.5l-.5 3h-2.5l-.5-2.5H8.5L7 18H3.5l.5-8z"/><path d="M10.5 10.5v1.5M19.5 6.5V5"/>');
export const AirRifleIcon = make("AirRifleIcon", '<path d="M23 9.5H12V8H7.5L4 10l-2 1.5.5 4.5h2L6 12.5h2l.5 1.5h2.5l.5-2H14l1-1h8z"/><path d="M8 5.5h7M9.5 5.5V8M13.5 5.5V8"/>');
export const MuzzleloaderIcon = make("MuzzleloaderIcon", '<path d="M23 9H12V7.5H8L4.5 9.5 2 11l.5 4.5h2L6 12h2l.5 1.5h2.5l.5-2H14l1-1h8z"/><path d="M9.5 7.5c-.5-2 1-3.5 2.5-2.5"/>');
export const ScopeIcon = make("ScopeIcon", '<path d="M3 9h3l1.5 1h7.5l2-2h4.5v8h-4.5l-2-2H7.5L6 15H3z"/><path d="M11.5 10V7.5M10 7.5h3M9 14v3.5M14.5 14v3.5"/>');
export const AmmoIcon = make("AmmoIcon", '<path d="M9 21V12l1.5-2V8c0-2.5 1-4 1.5-5 .5 1 1.5 2.5 1.5 5v2l1.5 2v9z"/><path d="M9 18.5h6"/>');
export const ArcheryIcon = make("ArcheryIcon", '<circle cx="12" cy="12" r="7"/><circle cx="12" cy="12" r="3"/><path d="M12 12l7.5-7.5M16 4.5h3.5V8"/>');
export const PartsIcon = make("PartsIcon", '<path d="M14.5 5.5a4 4 0 0 0-5.3 5.3l-6 6 2.5 2.5 6-6a4 4 0 0 0 5.3-5.3l-2.5 2.5-2.5-2.5z"/>');
export const AntiqueIcon = make("AntiqueIcon", '<path d="M2.5 15h10l3-3h6M12.5 15l-1 3.5h-3l1-3.5M9 12l2-3 2 3M17.5 9V7"/>');
export const SafeIcon = make("SafeIcon", '<rect x="4" y="3" width="16" height="18" rx="1"/><circle cx="14" cy="12" r="2.5"/><path d="M7.5 7.5v9"/>');
export const HuntingIcon = make("HuntingIcon", '<path d="M2.5 19.5l6.5-10 4 6 3-4 5.5 8z"/><circle cx="16" cy="6.5" r="1.8"/>');
export const ShieldCheckIcon = make("ShieldCheckIcon", '<path d="M12 3l7 3v5.5c0 4.5-3 8-7 9.5-4-1.5-7-5-7-9.5V6z"/><path d="M9 12l2 2 4-4"/>');
export const StoreIcon = make("StoreIcon", '<path d="M4 10l1.5-5h13L20 10M4 10v10h16V10M4 10c0 1.5 1.3 2.5 2.7 2.5S9.3 11.5 9.3 10c0 1.5 1.2 2.5 2.7 2.5s2.7-1 2.7-2.5c0 1.5 1.2 2.5 2.6 2.5S20 11.5 20 10M9.5 20v-6h5v6"/>');
export const ScaleIcon = make("ScaleIcon", '<path d="M12 3v18M4 8h16M6 8l-3 7h6l-3-7zM18 8l-3 7h6l-3-7zM8 21h8"/>');
export const SearchIcon = make("SearchIcon", '<circle cx="11" cy="11" r="6.5"/><path d="M16 16l5 5"/>');

export const CATEGORY_ICONS = {
  rifle: RifleIcon,
  shotgun: ShotgunIcon,
  handgun: HandgunIcon,
  air_rifle: AirRifleIcon,
  muzzleloader: MuzzleloaderIcon,
  scopes_optics: ScopeIcon,
  ammo: AmmoIcon,
  paintball_archery: ArcheryIcon,
  parts_accessories: PartsIcon,
  antique_replicas: AntiqueIcon,
  firearm_storage: SafeIcon,
  hunting_services: HuntingIcon,
};

/** Category key -> URL path, shared by header nav, footer and homepage tiles. */
export const CATEGORY_LINKS = [
  { key: "rifle", label: "Rifles", path: "/rifles" },
  { key: "shotgun", label: "Shotguns", path: "/shotguns" },
  { key: "handgun", label: "Handguns", path: "/handguns" },
  { key: "air_rifle", label: "Air Rifles", path: "/air-rifles" },
  { key: "muzzleloader", label: "Muzzleloaders", path: "/muzzleloaders" },
  { key: "scopes_optics", label: "Scopes & Optics", path: "/scopes-optics" },
  { key: "ammo", label: "Ammo", path: "/ammo" },
  { key: "paintball_archery", label: "Paintball & Archery", path: "/paintball-archery" },
  { key: "parts_accessories", label: "Parts & Accessories", path: "/parts-accessories" },
  { key: "antique_replicas", label: "Antique & Replicas", path: "/antique-replicas" },
  { key: "firearm_storage", label: "Storage", path: "/firearm-storage" },
  { key: "hunting_services", label: "Hunting Properties", path: "/hunting-services" },
];

export function CategoryIcon({ category, ...props }) {
  const Icon = CATEGORY_ICONS[category] || PartsIcon;
  return <Icon {...props} />;
}
